/**
 * CRM Me API slice (Workstream X Day 3).
 *
 *   GET /api/v1/crm/me?instanceId=<uuid>
 *
 * Returns the caller's resolved CRM-internal identity for an instance:
 * { crm_person_id, crm_role }. Built so role-aware frontend logic
 * (CrmLandingRedirect, future Day-11 Roles section, Day-12 auto-assign
 * config UI) doesn't reach into the link table directly.
 *
 * ## Caching
 *
 * `providesTags: ["CrmMe"]`. The tag will be invalidated by Day-11's
 * role-reassignment mutation when that ships (forward task captured at
 * WS-X Day 3 close). For v1, link-table rows are written at seed time
 * only, so cache is effectively immutable until Day 11.
 */
import { baseApi } from "./baseApi";

export type CrmRole = "counselor" | "manager" | "admin";

export interface CrmMeResponse {
  crm_person_id: string | null;
  crm_role: CrmRole | null;
  /**
   * E11 Inc-2 (read-only tenant mode). True when this instance is frozen
   * (export-only) — the FE shows a persistent banner and writes 423.
   * Optional for backward-compat with any cached/pre-Inc-2 response.
   */
  read_only?: boolean;
  /**
   * G9 §12 F8 item 1. True when this caller holds `consent_enforcement_admin`
   * — the capability that flips a jurisdiction's consent enforcement dial.
   *
   * It is resolved SERVER-side and shipped here rather than derived from
   * `usePermissions`, because every hook in that file admin-bypasses and this
   * key's whole premise is that a role which does not explicitly grant it
   * cannot flip statutory suppression (the same reason `clinical_read` is
   * excluded from that union). RENDER HINT ONLY — the router re-resolves the
   * capability on every request and is the enforcement point.
   */
  consent_enforcement_admin?: boolean;
}

export const crmMeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCrmMe: builder.query<CrmMeResponse, { instanceId: string }>({
      query: ({ instanceId }) => ({
        url: "/crm/me",
        params: { instanceId },
      }),
      providesTags: ["CrmMe"],
    }),
  }),
});

export const { useGetCrmMeQuery } = crmMeApi;
